import { BCMSDate, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useStateManager } from "@bcm-dev/bcms-vite";
import EDEStatementsPageContainer from "components/EDEStatements/shared/containers/EDEStatementsPageContainer";
import EDEStatementsPageContentView from "components/EDEStatements/shared/views/EDEStatementsPageContentView";
import { CenterContentContainer, ContentHeaderView } from 'components/shared';
import { PrimaryButton } from "components/ui";
import Classes from "model";
import React from "react";
import "./styles.css";
import "./mobile.css";


/**
 * @function EDEBatchDownloadPage
 * @return {React.Component}
 * @exported true
 * @description The ede "Batch Download" page.
 */

export default function EDEBatchDownloadPage(props) {
  // Initalize all model classes
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);

  // Sets the values for the data and ui variables
  const data = { ...getDefaultPageData(), ...props.data ?? {} };
  const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
  // The state to be passed to the state manager
  const state = { data, ui };
  
  // :: React.state, React.setState -> OBJECT
  // Function to initalize the controller for the state manager
  const controller = (state, setState) => ({
      data: state.data, // Set the data property of the controller
      ui: state.ui, // Set the ui property of the controller
      
      onChange: prop => val => {
        BCMSStateManagerCRUD.init(state)
        .update([ "ui", prop ], { isActive: state.ui[prop].isActive ? false : true })
        .submit(setState);
      }
  });
  
  return(
    <EDEStatementsPageContainer className={ `ede-batch-download-page` } state={ state } controller={ controller }>
        <PageContent/>
    </EDEStatementsPageContainer>
  );
}

/**
 * @name getDefaultPageData
 * @param {Void} 
 * @returns {OBJECT}
 * @description Hook to return the default data required by the page
 */
export const getDefaultPageData = () => {

  return {}
};

/**
* @name getDefaultPageUI
* @param {Void} 
* @return {OBJECT}
* @description Hook to return the default ui required by the page.
*/
export const getDefaultPageUI = () => ({
  // no ui changes for now
  checkingInput: { isActive: false },
  savingsInput: { isActive: false },
  // The select is a native element and may require a mouse event to show it open in a capture.
  //dateSelect: { isActive: false }
});

function PageContent(props) {
  const { data, ui, onChange } = useStateManager();
  const defaultDate = `${ BCMSDate.init().offset([0, -1, 0]).format("M, Y") }`;
  const searchButtonEnabled = ui.checkingInput.isActive || ui.savingsInput.isActive;
  const searchButton = enabled => {
    return enabled 
    ? <span>
        <DisabledPrompt>
          <div className="set-width">
            <PrimaryButton text="Search" />
          </div>
        </DisabledPrompt>
      </span>
    : <div className="set-width">
        <PrimaryButton className="disabled" text="Search" />
      </div>;
  };

  return (
    <EDEStatementsPageContentView>
      <div className="ede-batch-download-page-container flex-container flex-vertical flex-width">
        <CenterContentContainer>
          <ContentHeaderView text="All-In-One Batch Download" />
          <p className="description instructions">Select the designated month and account type(s) to retrieve applicable documents. Each document will be returned
            as individual items in a compressed (zipped) file. Extract files to access. The file naming convention is account type,
            last four digits of the account number, document date, and a sequence number if more than one document is found for that
            account and date.
          </p>
          <p className="description example">Example: Savings_1234_20180625_1.pdf</p>
          <p className="description account-type">To improve performance and reduce the time to return files, we recommend limiting to one account type per request.</p>

          <div className="flex-container flex-vertical">
            <div className="flex-width input-container checking">
              <label htmlFor="checking" className="flex-container align-center">
                <input id="checking" type="checkbox" className="set-width" checked={ ui.checkingInput.isActive } onChange={ onChange("checkingInput") }/>
                <span className="set-width">Checking</span>
              </label>
            </div>
            <div className="flex-width input-container savings">
              <label htmlFor="savings" className="flex-container align-center">
                <input id="savings" type="checkbox" className="set-width" checked={ ui.savingsInput.isActive } onChange={ onChange("savingsInput") }/>
                <span className="set-width">Savings</span>
              </label>
            </div>
            <div className="flex-width input-container">
              <select id="date-select">
                <option className="date-option">{ defaultDate }</option>
              </select>
            </div>
            <div className="flex-container button-container">{ searchButton(searchButtonEnabled) }</div>
          </div>
        </CenterContentContainer>
      </div>
    </EDEStatementsPageContentView>
  );
}
